import React, { useState, useEffect } from "react";
import {
  TextField,
  Checkbox,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RedCloseIcon from "../icons/RedCloseIcon";
import { motion } from "framer-motion";

const SearchWithCheckboxes = ({ data = [], isOutput, getSelectedItems }) => {
  console.log("data", data);
  const [searchTerm, setSearchTerm] = useState("");
  const [checkBox, setChecBox] = useState(false);

  const [items, setItems] = useState(
    Object.keys(data).map((item, index) => ({
      id: index,
      keys: item?.replace("_"," "),
      label: data[item],
      checked: false,
      disabled: false, // You can toggle this to true if you want specific items to be disabled.
    }))
  );
  const [selectAll, setSelectAll] = useState(false);

  // Update selected items whenever items change
  const selectedItems = items.filter((item) => item.checked);

  useEffect(() => {
    getSelectedItems(selectedItems);
  }, [checkBox, selectAll]);

  // Handle search filter
  const filteredItems = Array.isArray(items)
  ? items.filter((item) =>
      typeof item?.label === "string" &&
      item.label.toLowerCase().includes(searchTerm?.toLowerCase() || "")
    )
  : [];

  // Handle individual checkbox change
  const handleCheckboxChange = (id) => {
    setChecBox(!checkBox);
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  // Handle "Select All" checkbox change
  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.disabled ? item : { ...item, checked: !selectAll }
      )
    );
  };

  // Deselect a specific item (for isOutput case)
  const handleDeselectItem = (id) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, checked: false } : item
      )
    );
  };

  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: "12px",
        p: 2,
        backgroundColor: "white",
      }}
    >
      {/* Search Box */}
      <TextField
        placeholder="Search"
        variant="outlined"
        fullWidth
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#000" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: "25px",
            backgroundColor: "#fff",
          },
        }}
      />

      {/* "Select All" Checkbox */}
      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAll}
          icon={<CheckBoxOutlineBlankIcon />}
          checkedIcon={<CheckBoxIcon style={{ color: "black" }} />}
          sx={{
            color: selectAll ? "#000" : "#ccc",
            "&.Mui-checked": {
              color: "black",
            },
          }}
        />
        <Typography fontWeight="bold">Select All</Typography>
      </Box>
      <Divider />

      {/* Categories and Items */}
      <List>
        {filteredItems?.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={item.checked}
                disabled={item.disabled}
                onChange={() => handleCheckboxChange(item.id)}
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon style={{ color: "black" }} />}
              />
            </ListItemIcon>
            {/* Tooltip added to show label on hover */}
            <Tooltip
              title={
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 5 }} // Start slightly smaller & lower
                  animate={{ opacity: 1, scale: 1, y: 0 }} // Smooth scale up effect
                  exit={{ opacity: 0, scale: 0.9, y: 5 }} // Fade out effect
                  transition={{ duration: 0.3, ease: "easeOut" }} // Smooth timing
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "bold", p: 1 }}
                  >
                    {item.label}
                  </Typography>
                </motion.div>
              }
              arrow
              placement="bottom-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#333",
                    color: "#fff",
                    fontSize: "16px",
                    borderRadius: "8px",
                    padding: "10px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                  },
                },
                arrow: {
                  sx: {
                    color: "#333",
                    marginLeft: "-8px",
                  },
                },
              }}
            >
              <motion.div
                whileHover={{
                  backgroundColor: "#f0f0f0", // Light gray background on hover
                  scale: 1.05, // Slightly increase size
                  transition: { duration: 0.2 }, // Smooth transition
                }}
                style={{
                  borderRadius: "8px",
                  padding: "4px 8px",
                  cursor: "pointer",
                }}
              >
                <Typography>{item.keys}</Typography>
              </motion.div>
            </Tooltip>
            {isOutput && (
              <IconButton onClick={() => handleDeselectItem(item.id)}>
                <RedCloseIcon />
              </IconButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default SearchWithCheckboxes;
